import {useCallback} from 'react';

import {SPLITS_EVENTS} from '@/app/main/splits/events/SplitsEvents';
import {PROJECT_TAGS} from '@/constants/ProjectsConstants';
import {useTrackingManager} from '@/hooks';

export const useSplitsTrackers = (ownerId?: string, userId?: string) => {
  const {trackEvent} = useTrackingManager();

  const trackSplitsEvent = useCallback(
    (event: string, metadata?: Record<string, unknown>) => {
      trackEvent(event, {
        project_tag: PROJECT_TAGS.SPLITS,
        ...(ownerId && {
          user: {id: ownerId},
        }),
        ...(userId && {
          user: {id: userId},
        }),
        metadata,
      });
    },
    [ownerId, userId, trackEvent],
  );

  const trackViewSplitsLanding = useCallback(
    (splitId?: string | null, inviterName?: string) => {
      trackSplitsEvent(SPLITS_EVENTS.VIEW_SPLITS_LANDING, {
        split_id: splitId ?? undefined,
        inviter_name: inviterName ?? undefined,
      });
    },
    [trackSplitsEvent],
  );

  const trackViewSplitsSelectMember = useCallback(
    (splitId?: string | null, inviterName?: string) => {
      trackSplitsEvent(SPLITS_EVENTS.VIEW_SPLITS_SELECT_MEMBER, {
        split_id: splitId ?? undefined,
        inviter_name: inviterName ?? undefined,
      });
    },
    [trackSplitsEvent],
  );

  const trackSelectSplitsMember = useCallback(
    (splitId?: string | null, participantId?: string | null) => {
      trackSplitsEvent(SPLITS_EVENTS.SELECT_SPLITS_MEMBER, {
        split_id: splitId ?? undefined,
        participant_id: participantId ?? undefined,
      });
    },
    [trackSplitsEvent],
  );

  const trackSelectSplitsContinue = useCallback(
    (splitId?: string | null) => {
      trackSplitsEvent(SPLITS_EVENTS.SELECT_SPLITS_CONTINUE, {
        split_id: splitId ?? undefined,
      });
    },
    [trackSplitsEvent],
  );

  const trackViewSplitsPhoneInput = useCallback(
    (splitId?: string | null) => {
      trackSplitsEvent(SPLITS_EVENTS.VIEW_SPLITS_PHONE_INPUT, {
        split_id: splitId ?? undefined,
      });
    },
    [trackSplitsEvent],
  );

  const trackSelectSplitsBackPhoneInput = useCallback(
    (splitId?: string | null) => {
      trackSplitsEvent(SPLITS_EVENTS.SELECT_SPLITS_BACK_PHONE_INPUT, {
        split_id: splitId ?? undefined,
      });
    },
    [trackSplitsEvent],
  );

  const trackSelectSplitsPhoneContinue = useCallback(
    (splitId?: string | null) => {
      trackSplitsEvent(SPLITS_EVENTS.SELECT_SPLITS_PHONE_CONTINUE, {
        split_id: splitId ?? undefined,
      });
    },
    [trackSplitsEvent],
  );

  const trackViewSplitsOtp = useCallback(
    (splitId?: string | null) => {
      trackSplitsEvent(SPLITS_EVENTS.VIEW_SPLITS_OTP, {
        split_id: splitId ?? undefined,
      });
    },
    [trackSplitsEvent],
  );

  const trackSelectSplitsResendOtp = useCallback(
    (splitId?: string | null) => {
      trackSplitsEvent(SPLITS_EVENTS.SELECT_SPLITS_RESEND_OTP, {
        split_id: splitId ?? undefined,
      });
    },
    [trackSplitsEvent],
  );

  const trackSelectSplitsSubmitOtp = useCallback(
    (splitId?: string | null) => {
      trackSplitsEvent(SPLITS_EVENTS.SELECT_SPLITS_SUBMIT_OTP, {
        split_id: splitId ?? undefined,
      });
    },
    [trackSplitsEvent],
  );

  const trackSelectSplitsOpenAccount = useCallback(
    (splitId?: string | null) => {
      trackSplitsEvent(SPLITS_EVENTS.SELECT_SPLITS_OPEN_ACCOUNT, {
        split_id: splitId ?? undefined,
      });
    },
    [trackSplitsEvent],
  );

  const trackApiSplitsGetDataSuccess = useCallback(
    (splitId?: string | null) => {
      trackSplitsEvent(SPLITS_EVENTS.API_SPLITS_GET_DATA_SUCCESS, {
        split_id: splitId ?? undefined,
      });
    },
    [trackSplitsEvent],
  );

  const trackApiSplitsGetDataError = useCallback(
    (splitId?: string | null, error?: string) => {
      trackSplitsEvent(SPLITS_EVENTS.API_SPLITS_GET_DATA_ERROR, {
        split_id: splitId ?? undefined,
        error: error ?? undefined,
      });
    },
    [trackSplitsEvent],
  );

  const trackSelectSplitsGoToPay = useCallback(
    (splitId?: string | null, participantId?: string | null, url?: string) => {
      trackSplitsEvent(SPLITS_EVENTS.SELECT_SPLITS_GO_TO_PAY, {
        split_id: splitId ?? undefined,
        participant_id: participantId ?? undefined,
        url: url ?? undefined,
      });
    },
    [trackSplitsEvent],
  );

  const trackViewSplitsConfirmation = useCallback(
    (splitId?: string | null, participantId?: string | null) => {
      trackSplitsEvent(SPLITS_EVENTS.VIEW_SPLITS_CONFIRMATION, {
        split_id: splitId ?? undefined,
        participant_id: participantId ?? undefined,
      });
    },
    [trackSplitsEvent],
  );

  const trackViewSplitsError = useCallback(
    (splitId?: string | null, error?: string) => {
      trackSplitsEvent(SPLITS_EVENTS.VIEW_SPLITS_ERROR, {
        split_id: splitId ?? undefined,
        error: error ?? undefined,
      });
    },
    [trackSplitsEvent],
  );

  const trackViewSplitsSuccess = useCallback(
    (splitId?: string | null, participantId?: string | null) => {
      trackSplitsEvent(SPLITS_EVENTS.VIEW_SPLITS_SUCCESS, {
        split_id: splitId ?? undefined,
        participant_id: participantId ?? undefined,
      });
    },
    [trackSplitsEvent],
  );

  return {
    trackViewSplitsLanding,
    trackViewSplitsSelectMember,
    trackSelectSplitsMember,
    trackSelectSplitsContinue,
    trackViewSplitsPhoneInput,
    trackSelectSplitsBackPhoneInput,
    trackSelectSplitsPhoneContinue,
    trackViewSplitsOtp,
    trackSelectSplitsResendOtp,
    trackSelectSplitsSubmitOtp,
    trackSelectSplitsOpenAccount,
    trackApiSplitsGetDataSuccess,
    trackApiSplitsGetDataError,
    trackSelectSplitsGoToPay,
    trackViewSplitsConfirmation,
    trackViewSplitsError,
    trackViewSplitsSuccess,
  };
};
